import React from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Breadcrumb, Button as ButtonAntd, Badge, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Box from '~/components/Box';
import Pagination from '~/components/Pagination';
import { Table } from '~/components/Table';
import { BreadcrumbCustom } from '~/styles/global';
import Filter from './filter';
import DefautLayout from '~/pages/_layouts/full';
import history from '~/services/history';
import { findAllProjectTypes, paginationProjects } from '~/services/hooks/projects';


export default function ClientProjects() {
    const profile = useSelector(state => state.user.profile);
    const customerId = profile.customerId;
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({});
    const [tableData, setTableData] = useState([]);
    const [filters, setFilters] = useState([]);
    const defaultFilterCustomer = customerId !== undefined ? { columnJoin: 'customer', field: 'id', value: customerId, restriction: 'EQUAL' } : {};

    const fetchData = async page => {
        setLoading(true);
        try {
        const data = await paginationProjects({
            page: page - 1,
            sortBy: 'id',
            filters: [...filters, defaultFilterCustomer], // filters: [...filters, defaultFilter, defaultFilterCustomer],
        });

        const {
            first,
            last,
            numberOfElements,
            totalElements,
            pageable: { pageNumber, pageSize },
            content, 
            } = data;

        setTableData(content);

        setMeta({
            first,
            last,
            numberOfElements,
            pageNumber: pageNumber + 1,
            pageSize,
            totalElements,
        });

        } catch (error) {
            console.error('Error fetching projects:', error);
            message.error(t('messages:errorFetchingProjects'));
        }
        finally {
        setLoading(false);
        }
    };

    const fetchProjectTypes = async () => {
        setLoading(true);
        try{
            await findAllProjectTypes();
        } catch (error) {
            console.error('Error fetching project types:', error);
            message.error(t('messages:errorFetchingProjectTypes'));
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjectTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const style = {
        breadcrumb: { margin: '16px 0' },
        pageTitle: { marginRight: 'auto', width: '100%' },
        btnSubmit: { marginLeft: 10, marginRight: 10, marginTop: 25 },
        btnClear: { marginLeft: 'auto', marginTop: 25 },
        btnNew: {
            col: { marginLeft: 'auto', paddingBottom: '20px' },
            btn: { marginLeft: 'auto' }
        },
        status: {
            new: { backgroundColor: '#1890FF' },
            open: { backgroundColor: '#13C2C2' },
            inProgress: { backgroundColor: '#A0D911' },
            completed: { backgroundColor: '#FAAD14' },
            onHold: { backgroundColor: '#F5222D' }
        }
    }

    function getStyle(value) {
        switch (value) {
            case 'open':
                return (style.status.open);
            case 'in_progress':
                return (style.status.inProgress);
            case 'completed':
                return (style.status.completed);
            case 'on_hold':
                return (style.status.onHold);
            default:
                return (style.status.new);
        }
    }

    const projectStatus = [
        {
            id: 0,
            code: 'ALL',
            description: t('screens:status.all'),
        },
        {
            id: 1,
            code: 'NEW',
            description: t('screens:projects.data.status.new'),
        },
        {
            id: 2,
            code: 'OPEN',
            description: t('screens:projects.data.status.open'),
        },
        {
            id: 3,
            code: 'IN_PROGRESS',
            color: '',
            description: t('screens:projects.data.status.in_progress'),
        },
        {
            id: 4,
            code: 'COMPLETED',
            description: t('screens:projects.data.status.completed'),
        },
        {
            id: 5,
            code: 'ON_HOLD',
            description: t('screens:projects.data.status.on_hold'),
        },
    ];

    const tableColumns = [
        {
            title: t('screens:projects.data.code'),
            dataIndex: 'id',
            key: 'id',
            align: 'center',
            width: 70,
        },
        {
            title: t('screens:projects.data.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('screens:projects.data.type'),
            dataIndex: 'typeDescription',
            key: 'typeDescription',
        },
        {
            title: t('screens:projects.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: '120px',
            render: status =>
                <Badge
                    count={(t('screens:projects.data.status.' + status.toLowerCase()).toUpperCase())}
                    style={getStyle(status.toLowerCase())} />
        },
        {
            title: t('screens:projects.data.actions'),
            key: 'actions',
            align: 'center',
            width: 85,
            ellipsis: { showTitle: true },
            render: record => (
              <ButtonAntd
                title={t('messages:view')}
                type="link"
                icon={<EyeOutlined />}
                onClick={() =>
                  history.push({
                    pathname: '/client-project-details',
                    state: { project: record, customer: record.customer },
                  })
                }
              />
            ),
          },
    ];

    const breadcrumb = (
        <BreadcrumbCustom>
            <Breadcrumb.Item href="/">{t('menus:home')}</Breadcrumb.Item>
            <Breadcrumb.Item>{t('menus:projects')}</Breadcrumb.Item>
        </BreadcrumbCustom>
    );

    return (
        <DefautLayout breadcrumb={breadcrumb}>
            <Filter projectStatus={projectStatus} isLoading={loading} setFilters={setFilters} />

            <Box>
                <Table
                    bordered
                    size="small"
                    rowKey="id"
                    pagination={false}
                    loading={loading}
                    columns={tableColumns}
                    dataSource={tableData}
                />

                <Pagination
                    onChange={value => {
                        fetchData(value);
                    }}
                    meta={meta}
                />
            </Box>
        </DefautLayout>
    );
}